import CloseIcon from '@mui/icons-material/Close'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import { Box, Button, ClickAwayListener, IconButton, Popover, Stack, Typography, useTheme } from '@mui/material'
import { useNotificationsByUserId } from 'api/aws/notifications/useNotificationsByUserId'
import { useEntitiesFromSearch } from 'api/search'
import format from 'date-fns/format'
import React, { useEffect, useMemo, useState } from 'react'
import Notification from './Notification'

function formatDateTime(ts: string | number | Date) {
  return format(new Date(ts), 'MMM dd h:mm a')
}

type Markable = { PK: string; SK: string; notify_uuid: string }

const NotificationDrawer = ({ userId, drawerOpen, closeDrawer, anchorEl }) => {
  const theme = useTheme()
  const { data: rawData, isLoading } = useNotificationsByUserId(userId, drawerOpen)
  const { organizations, deals } = useEntitiesFromSearch()

  const notifications = useMemo(
    () => (rawData?.notifications ?? []).filter(n => !n.isRead), // ← only unread for now
    [rawData]
  )
  const sortedUnread = useMemo(
    () => [...notifications].sort((a, b) => +new Date(b.creation_timestamp) - +new Date(a.creation_timestamp)),
    [notifications]
  )

  // Which ones the user explicitly marked during this session
  const [markedIds, setMarkedIds] = useState<Set<string>>(new Set())
  const [markedObjs, setMarkedObjs] = useState<Markable[]>([])

  useEffect(() => {
    if (!drawerOpen) {
      setMarkedIds(new Set())
      setMarkedObjs([])
    }
  }, [drawerOpen])

  const toggleMark = (n: any) => {
    setMarkedIds(prev => {
      const next = new Set(prev)
      if (next.has(n.notify_uuid)) {
        next.delete(n.notify_uuid)
        setMarkedObjs(objs => objs.filter(o => o.notify_uuid !== n.notify_uuid))
      } else {
        next.add(n.notify_uuid)
        setMarkedObjs(objs => [...objs, { PK: n.PK, SK: n.SK, notify_uuid: n.notify_uuid }])
      }
      return next
    })
  }

  const markAll = () => {
    const allIds = new Set(sortedUnread.map(n => n.notify_uuid))
    setMarkedIds(allIds)
    setMarkedObjs(sortedUnread.map(n => ({ PK: n.PK, SK: n.SK, notify_uuid: n.notify_uuid })))
  }

  const handleClose = () => {
    closeDrawer(markedObjs) // parent persists
  }
  return (
    <Box>
      <Popover
        open={drawerOpen}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          root: {
            sx: {
              pointerEvents: 'auto',
              zIndex: 1699, // Ensure it's above other elements
              transition: 'all 0.3s ease',
            },
          },
          paper: {
            sx: {
              bgcolor: 'background.paper',
              borderRadius: 2,
              width: '36vw',
              maxWidth: 560,
              height: '80vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: 6,
              overflow: 'hidden',
            },
          },
        }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Stack sx={{ height: '100%' }}>
            {/* Header */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid',
                borderColor: 'divider',
                position: 'sticky',
                top: 0,
                backgroundColor: 'background.paper',
                zIndex: 1,
              }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                Notifications
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Unread: {sortedUnread.length}
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<DoneAllIcon />}
                  onClick={markAll}
                  disabled={sortedUnread.length === 0}
                >
                  Mark all as read
                </Button>
                <IconButton size="small" onClick={handleClose} aria-label="close">
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Box>

            {/* List */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                p: 2,
                pt: 1,
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  boxShadow:
                    theme.palette.mode === 'dark'
                      ? 'inset 0 0 6px rgba(0,0,0,0.3)'
                      : 'inset 0 0 6px rgba(255,255,255,0.3)',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor:
                    theme.palette.mode === 'dark' ? theme.palette.background.scrollbar_thumb : 'rgba(0,0,0,0.5)',
                  borderRadius: '6px',
                },
              }}
            >
              {isLoading ? (
                <Typography variant="body2" color="text.secondary">
                  Loading…
                </Typography>
              ) : sortedUnread.length === 0 ? (
                <Box
                  sx={{
                    p: 3,
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    textAlign: 'center',
                  }}
                >
                  <Typography fontWeight={600}>No new notifications</Typography>
                  <Typography variant="body2" color="text.secondary">
                    You’re all caught up.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1.2}>
                  {sortedUnread.map(n => {
                    const relatedBoard = (n?.board || '').toLowerCase()
                    const relatedOrg = organizations?.find(o => o?.org_uuid === n?.boardId)
                    const relatedDeal = deals?.find(d => d?.deal_uuid === n?.boardId)
                    const baseUrl =
                      relatedBoard === 'deals'
                        ? `/deals/${relatedOrg?.SK}/${relatedDeal?.SK}/comments`
                        : relatedBoard === 'tasks' || relatedBoard === 'teamtasks'
                        ? n?.url
                        : `/${relatedBoard}/${relatedOrg?.PK}/comments`

                    // const originRoute = n?.origin
                    return (
                      <React.Fragment key={n?.SK}>
                        <Notification
                          notification={n}
                          url={n?.slackDirectLink}
                          marked={markedIds.has(n.notify_uuid)}
                          onMarkRead={() => toggleMark(n)}
                          closeDrawer={handleClose}
                        />
                      </React.Fragment>
                    )
                  })}
                </Stack>
              )}
            </Box>
          </Stack>
        </ClickAwayListener>
      </Popover>
    </Box>
  )
}

export default NotificationDrawer
